import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
  CheckCircle as HealthyIcon,
  ErrorOutline as CriticalIcon,
  Factory as FactoryIcon
} from '@mui/icons-material';
import { FacilityCode } from '@gff/shared';
import {
  fetchGlobalTelemetryMatrix,
  IGlobalTelemetryMatrix,
  IFacilitySummaryTelemetry,
  IFacilityItemTelemetry
} from '../../services/inventoryTelemetryService';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';

export const GlobalInventoryMatrix: React.FC = () => {
  const { user } = useAuth();
  const { socket, connected } = useSocket();

  const [data, setData] = useState<IGlobalTelemetryMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'ALL' | FacilityCode>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState<string>('');

  const isSuperAdmin = user?.role === 'super_admin';
  const assignedRegion = user?.assignedRegion?.toUpperCase();

  // Load telemetry data
  const loadTelemetry = async () => {
    try {
      setLoading(true);
      const matrix = await fetchGlobalTelemetryMatrix();
      if (matrix) {
        setData(matrix);
        setLastRefreshed(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error('[GlobalInventoryMatrix] Failed to load global telemetry:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTelemetry();

    // Default tab based on regional admin assignment if not super admin
    if (!isSuperAdmin) {
      if (assignedRegion === 'UAE') {
        setSelectedTab('UAE-DXB');
      } else {
        const hub = (user?.hub || user?.assignedCity || user?.city || '').toLowerCase();
        if (hub.includes('lahore') || hub.includes('lhe')) {
          setSelectedTab('PK-LHE');
        } else {
          setSelectedTab('PK-KHI');
        }
      }
    }
  }, [user, isSuperAdmin, assignedRegion]);

  // WebSocket Live Subscription
  useEffect(() => {
    if (!socket) return;

    const handleAlert = (payload: any) => {
      console.log('[GlobalInventoryMatrix] Live alert received:', payload);
      // Soft reload telemetry without jarring full loading state
      fetchGlobalTelemetryMatrix().then((matrix) => {
        if (matrix) {
          setData(matrix);
          setLastRefreshed(new Date().toLocaleTimeString());
        }
      });
    };

    socket.on('inventory:facility_alert', handleAlert);
    socket.on('inventory:stockout_warning', handleAlert);

    return () => {
      socket.off('inventory:facility_alert', handleAlert);
      socket.off('inventory:stockout_warning', handleAlert);
    };
  }, [socket]);

  // Consolidate or filter items
  const displayItems = useMemo(() => {
    if (!data) return [];

    let rawList: (IFacilityItemTelemetry & { facility: FacilityCode })[] = [];

    if (selectedTab === 'ALL') {
      const facilities: FacilityCode[] = ['PK-KHI', 'PK-LHE', 'UAE-DXB'];
      for (const fac of facilities) {
        const facData = data.facilities?.[fac];
        if (facData?.items) {
          rawList.push(...facData.items.map(it => ({ ...it, facility: fac })));
        }
      }
    } else {
      const facData = data.facilities?.[selectedTab];
      if (facData?.items) {
        rawList = facData.items.map(it => ({ ...it, facility: selectedTab }));
      }
    }

    if (!searchQuery.trim()) return rawList;

    const q = searchQuery.toLowerCase().trim();
    return rawList.filter(
      it =>
        it.ingredientCode.toLowerCase().includes(q) ||
        it.displayName.toLowerCase().includes(q)
    );
  }, [data, selectedTab, searchQuery]);

  const getDosBadge = (dos: number, onHand: number, threshold: number) => {
    if (onHand <= 0 || dos < 2.0) {
      return (
        <Chip
          icon={<CriticalIcon fontSize="small" />}
          label={dos >= 900 ? 'Stockout' : `${dos}d DOS`}
          color="error"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      );
    }
    if (onHand <= threshold || dos < 5.0) {
      return (
        <Chip
          icon={<WarningIcon fontSize="small" />}
          label={`${dos}d DOS`}
          color="warning"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      );
    }
    return (
      <Chip
        icon={<HealthyIcon fontSize="small" />}
        label={dos >= 900 ? 'Abundant' : `${dos}d DOS`}
        color="success"
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  const getFacilityColor = (facility: FacilityCode) => {
    switch (facility) {
      case 'PK-KHI':
        return '#0284c7'; // Sky/Ocean Blue
      case 'PK-LHE':
        return '#16a34a'; // Emerald Green
      case 'UAE-DXB':
        return '#d97706'; // Amber / Gold
      default:
        return '#64748b';
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const khi = data?.facilities?.['PK-KHI'];
  const lhe = data?.facilities?.['PK-LHE'];
  const dxb = data?.facilities?.['UAE-DXB'];

  return (
    <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <CardContent>
        {/* Header Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FactoryIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Multi-Facility Independent Inventory & Global Cockpit
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Isolated facility stock ledgers for Karachi, Lahore & Dubai with unified 14-day trailing burn rate oversight.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={connected ? 'Live Sync Active' : 'Polling Sync'}
              color={connected ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
            {lastRefreshed && (
              <Typography variant="caption" color="text.secondary">
                Updated {lastRefreshed}
              </Typography>
            )}
            <Tooltip title="Refresh Telemetry">
              <IconButton size="small" onClick={loadTelemetry} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Top Operational Matrix Cards */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Card 1: Karachi Flagship */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: selectedTab === 'PK-KHI' ? '#0284c7' : 'divider',
                borderRadius: 2,
                bgcolor: selectedTab === 'PK-KHI' ? 'rgba(2, 132, 199, 0.04)' : 'background.paper',
                transition: 'all 0.2s ease'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0284c7' }}>
                  🇵🇰 Karachi Hub (PK-KHI)
                </Typography>
                <Chip
                  label={khi?.totalAlertsCount ? `${khi.totalAlertsCount} Alerts` : 'Nominal'}
                  color={khi?.totalAlertsCount ? 'error' : 'success'}
                  size="small"
                />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {khi?.totalOnHandUnits ? khi.totalOnHandUnits.toLocaleString() : '0'} <Typography component="span" variant="body2" color="text.secondary">kg on hand</Typography>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Active SKUs: <strong>{khi?.totalSkus ?? 0}</strong></Typography>
                <Typography variant="caption" color="text.secondary">Avg Supply: <strong>{khi?.averageDaysOfSupply ?? 0}d</strong></Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Card 2: Lahore Regional Hub */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: selectedTab === 'PK-LHE' ? '#16a34a' : 'divider',
                borderRadius: 2,
                bgcolor: selectedTab === 'PK-LHE' ? 'rgba(22, 163, 74, 0.04)' : 'background.paper',
                transition: 'all 0.2s ease'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#16a34a' }}>
                  🇵🇰 Lahore Hub (PK-LHE)
                </Typography>
                <Chip
                  label={lhe?.totalAlertsCount ? `${lhe.totalAlertsCount} Alerts` : 'Nominal'}
                  color={lhe?.totalAlertsCount ? 'error' : 'success'}
                  size="small"
                />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {lhe?.totalOnHandUnits ? lhe.totalOnHandUnits.toLocaleString() : '0'} <Typography component="span" variant="body2" color="text.secondary">kg on hand</Typography>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Active SKUs: <strong>{lhe?.totalSkus ?? 0}</strong></Typography>
                <Typography variant="caption" color="text.secondary">Avg Supply: <strong>{lhe?.averageDaysOfSupply ?? 0}d</strong></Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Card 3: Dubai DIP Hub */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: selectedTab === 'UAE-DXB' ? '#d97706' : 'divider',
                borderRadius: 2,
                bgcolor: selectedTab === 'UAE-DXB' ? 'rgba(217, 119, 6, 0.04)' : 'background.paper',
                transition: 'all 0.2s ease'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#d97706' }}>
                  🇦🇪 Dubai Hub (UAE-DXB)
                </Typography>
                <Chip
                  label={dxb?.totalAlertsCount ? `${dxb.totalAlertsCount} Alerts` : 'Nominal'}
                  color={dxb?.totalAlertsCount ? 'error' : 'success'}
                  size="small"
                />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {dxb?.totalOnHandUnits ? dxb.totalOnHandUnits.toLocaleString() : '0'} <Typography component="span" variant="body2" color="text.secondary">kg on hand</Typography>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Active SKUs: <strong>{dxb?.totalSkus ?? 0}</strong></Typography>
                <Typography variant="caption" color="text.secondary">Avg Supply: <strong>{dxb?.averageDaysOfSupply ?? 0}d</strong></Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Filter Controls & Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(_, val) => setSelectedTab(val)}
            textColor="primary"
            indicatorColor="primary"
          >
            {isSuperAdmin && <Tab label="Consolidated Global Matrix" value="ALL" />}
            <Tab label="Karachi (PK-KHI)" value="PK-KHI" />
            <Tab label="Lahore (PK-LHE)" value="PK-LHE" />
            <Tab label="Dubai (UAE-DXB)" value="UAE-DXB" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search ingredient or code..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
        </Box>

        {/* Detailed Item Telemetry Table */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Facility</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ingredient / SKU</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>On-Hand</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Allocated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>14D Daily Burn</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Days of Supply (DOS)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Safety Threshold</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No facility stock items matched the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayItems.map((item) => (
                  <TableRow key={`${item.facility}-${item.ingredientId}`} hover>
                    <TableCell>
                      <Chip
                        label={item.facility}
                        size="small"
                        sx={{
                          bgcolor: `${getFacilityColor(item.facility)}15`,
                          color: getFacilityColor(item.facility),
                          fontWeight: 'bold',
                          border: `1px solid ${getFacilityColor(item.facility)}40`
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.ingredientCode}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {item.onHand.toFixed(2)} {item.unit}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {item.allocated.toFixed(2)} {item.unit}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {item.dailyBurnRate14d > 0 ? `${item.dailyBurnRate14d.toFixed(2)} ${item.unit}/d` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getDosBadge(item.daysOfSupply, item.onHand, item.lowStockThreshold)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {item.lowStockThreshold} {item.unit} (Reorder: {item.reorderQuantity})
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default GlobalInventoryMatrix;
